import { useState } from 'react';
import { BookingsModal } from '@/components/bookings/BookingsModal';
import { StatCard } from '@/components/ui/stat-card';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { useBookings } from '@/lib/queries';

/*
 * "Bookinger" KPI box — named Momence bookings for the visible range. Click the
 * card to open the full BookingsModal drill-down (who booked, when, from which
 * ad + campaign + how the link was established, internal tests). Replaced the old
 * cramped hover popover so the data has real room.
 */

const MESSAGES = {
  nb: {
    label: 'Bookinger',
    subSplit: (real: string, internal: string) => `${real} reelle · ${internal} interne`,
    errorHint: 'Fikk ikke hentet bookinger.',
  },
  en: {
    label: 'Bookings',
    subSplit: (real: string, internal: string) => `${real} real · ${internal} internal`,
    errorHint: 'Could not load bookings.',
  },
};

export function BookingsKpi() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const q = useBookings(from, to);
  const [open, setOpen] = useState(false);

  const counts = q.data?.counts ?? null;
  const range = `${formatIsoDate(from).date} → ${formatIsoDate(to).date}`;
  const sub = q.isError
    ? m.errorHint
    : counts && counts.internal > 0
      ? m.subSplit(
          formatMetric(counts.total - counts.internal, 'int'),
          formatMetric(counts.internal, 'int'),
        )
      : range;
  const clickable = !!q.data;

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: a real <button> can't wrap StatCard (block content violates button's phrasing-content model); Enter/Space open the dialog. */}
      <div
        role="button"
        tabIndex={clickable ? 0 : -1}
        aria-haspopup="dialog"
        onClick={() => clickable && setOpen(true)}
        onKeyDown={(e) => {
          if (clickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={clickable ? 'cursor-pointer' : ''}
      >
        <StatCard
          label={
            <span
              className={
                clickable
                  ? 'decoration-fg-subtle decoration-dotted underline-offset-2 hover:underline'
                  : ''
              }
            >
              {m.label}
            </span>
          }
          source="momence"
          value={
            <span className="text-title">{formatMetric(counts ? counts.total : null, 'int')}</span>
          }
          sub={sub}
        />
      </div>
      {q.data ? (
        <BookingsModal
          open={open}
          onClose={() => setOpen(false)}
          bookings={q.data.bookings}
          rangeLabel={range}
          counts={counts}
        />
      ) : null}
    </>
  );
}
